import {
  AddIcon,
  Button,
  Hide,
  SubtractIcon,
  Flex,
  Typography,
  CheckIcon,
} from '@iheartjane/reefer';
import type { ProductCardCtaStyle, ProductCardSize } from '@iheartjane/types';
import { clsx } from 'clsx';

import styles from './editQuantityButton.module.css';

export interface EditQuantityButtonProps {
  atMaxQuantity?: boolean;
  cardSize?: ProductCardSize;
  cartCount: number;
  ctaStyle?: Exclude<ProductCardCtaStyle, 'card_bottom'>;
  isUpdatingCart?: boolean;
  onDecrease?: () => void;
  onIncrease?: () => void;
  productName?: string;
}

const ctaStyleClass: Record<string, keyof typeof styles> = {
  card_bottom_full: 'cardBottomFull',
  image_bottom: 'imageBottom',
  image_top: 'imageTop',
};

export function EditQuantityButton({
  atMaxQuantity,
  cartCount,
  ctaStyle = 'card_bottom_full',
  isUpdatingCart,
  cardSize,
  onIncrease,
  onDecrease,
  productName,
}: EditQuantityButtonProps) {
  const subtractDisabled = isUpdatingCart || cartCount === 0;
  const addDisabled = isUpdatingCart || atMaxQuantity;

  return (
    <Flex
      role="group"
      aria-label={productName ? `Quantity for ${productName}` : 'Quantity'}
      className={clsx(styles[ctaStyleClass[ctaStyle]], {
        [styles['updating']]: isUpdatingCart,
        [styles[
          `${ctaStyleClass[ctaStyle]}_${cardSize}` as keyof typeof styles
        ]]: cardSize,
      })}
      justifyContent="center"
      alignItems="center"
      background="primary"
      borderRadius="lg"
    >
      <Button.Icon
        className={styles['quantityIcon']}
        aria-label={
          cartCount === 1 ? 'Remove from bag' : 'Reduce quantity by 1'
        }
        icon={
          <SubtractIcon
            className={
              subtractDisabled
                ? styles['textDisabledColor']
                : styles['textContrastColor']
            }
            size="sm"
          />
        }
        disabled={subtractDisabled}
        onClick={onDecrease}
      />
      <div
        className={styles['centerDisplay']}
        style={ctaStyle === 'card_bottom_full' ? { width: '48px' } : undefined}
        aria-live="polite"
        aria-atomic="true"
      >
        <CheckIcon
          aria-hidden="true"
          className={clsx(
            styles['textContrastColor'],
            isUpdatingCart ? styles['visible'] : styles['hidden']
          )}
          color="inherit"
          size={ctaStyle === 'image_bottom' ? 'sm' : 'md'}
        />
        <Typography
          className={clsx(
            styles['textContrastColor'],
            isUpdatingCart ? styles['hidden'] : styles['visible']
          )}
          textAlign="center"
        >
          <Hide as="span" isHidden>
            quantity:{' '}
          </Hide>
          {cartCount}
        </Typography>
      </div>
      <Button.Icon
        className={styles['quantityIcon']}
        aria-label={atMaxQuantity ? 'Limit reached' : 'Increase quantity by 1'}
        icon={
          <AddIcon
            className={
              addDisabled
                ? styles['textDisabledColor']
                : styles['textContrastColor']
            }
            size="sm"
          />
        }
        disabled={addDisabled}
        onClick={onIncrease}
      />
    </Flex>
  );
}
